import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { Ban, X } from 'lucide-react';

const AdminReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    adminService.listReports().then((data) => {
      setReports(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleBlock = async (reportedId) => {
    setActing(reportedId);
    try {
      await adminService.setBlocked(reportedId, true);
      setReports((prev) =>
        prev.map((r) => (r.reported_id === reportedId ? { ...r, reported: { ...r.reported, is_blocked: true } } : r))
      );
    } catch (err) {
      console.error(err);
    }
    setActing(null);
  };

  const handleDismiss = async (reportId) => {
    setActing(reportId);
    try {
      await adminService.dismissReport(reportId);
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err) {
      console.error(err);
    }
    setActing(null);
  };

  const reporterName = (r) => r.reporter?.display_name || r.reporter?.email || r.reporter_id || '—';
  const reportedName = (r) => r.reported?.display_name || r.reported?.email || r.reported_id || '—';

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">Reports</h1>
      <p className="text-slate-600 mb-6">Review reports and block users or dismiss.</p>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      ) : reports.length === 0 ? (
        <div className="p-8 rounded-xl bg-white border border-slate-200 text-center text-slate-500">
          No reports.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-medium text-slate-700">Reporter</th>
                <th className="text-left p-3 font-medium text-slate-700">Reported</th>
                <th className="text-left p-3 font-medium text-slate-700">Reason</th>
                <th className="text-left p-3 font-medium text-slate-700">Date</th>
                <th className="text-left p-3 font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">{reporterName(r)}</td>
                  <td className="p-3">
                    <Link to={`/admin/users/${r.reported_id}`} className="text-primary hover:underline">
                      {reportedName(r)}
                    </Link>
                    {r.reported?.is_blocked && (
                      <span className="ml-1 text-red-600 text-xs">(blocked)</span>
                    )}
                  </td>
                  <td className="p-3 max-w-xs truncate">{r.reason || '—'}</td>
                  <td className="p-3">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                  <td className="p-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleBlock(r.reported_id)}
                      disabled={acting === r.reported_id || r.reported?.is_blocked}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      <Ban className="h-3 w-3" />
                      Block user
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDismiss(r.id)}
                      disabled={acting === r.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                      Dismiss
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminReportsPage;
